import type { ToolCallMessagePart } from '@assistant-ui/react';
import { Loader } from '@mantine/core';

import classes from './ProgressBubble.module.css';
import { Bubble } from '#plugin-chat/components/bubble/Bubble.tsx';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { getProgressComponent } from '#plugin-chat/lib/tool/progress-registry.ts';

interface ProgressBubbleProps {
  // biome-ignore lint/suspicious/noExplicitAny: Runtime part — per-tool types are erased at this layer.
  toolPart?: ToolCallMessagePart<any, any>;
  fallbackComponent?: React.ComponentType | null;
}

/**
 * Activity indicator rendered for the currently running tool in a collapsible run.
 *
 * Shape: "AiPSO Agent is {progress component output}". The progress component is
 * looked up from the module-level registry (populated by `createTool` when a tool
 * declares a `progress` FC on its config). Renders nothing when no component is
 * registered — non-collapsible tools never reach this path.
 */
export function ProgressBubble({ toolPart, fallbackComponent = GenericProgress }: ProgressBubbleProps) {
  const ProgressComponent = getProgressComponent(toolPart?.toolName) ?? fallbackComponent;

  if (!ProgressComponent) {
    return null;
  }

  return (
    <Bubble corners={[]} role="assistant">
      <div className={classes.loader}>
        <Loader color="dark.1" size="xs" type="dots" />
        <Shimmer className={classes.label}>
          AiPSO Agent is{' '}
          <ProgressComponent args={toolPart?.args} result={toolPart?.result} toolCallId={toolPart?.toolCallId} />
        </Shimmer>
      </div>
    </Bubble>
  );
}

function GenericProgress() {
  return <>thinking...</>;
}
